import { Link, Navigate, useParams } from 'react-router-dom';
import { useMatches, type MatchEntry } from './useMatches';
import { AlbumTabs } from '../albums/AlbumTabs';
import { Avatar } from '../theme/icons';
import { teamThemeOf } from '../theme/teamPalette';

export default function FriendsPage() {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) return <Navigate to="/albums" replace />;

  const { data: matches, isLoading, error } = useMatches(slug);

  return (
    <div className="min-h-full safe-bottom">
      <AlbumTabs slug={slug} active="friends" />

      <div className="px-4 pt-4 pb-3">
        <h1 className="font-sans text-[22px] font-extrabold text-text leading-tight">Tausch</h1>
        <p className="font-sans text-[13px] text-muted mt-0.5">Wer passt zu dir?</p>
      </div>

      <div className="px-4 pb-6 space-y-2.5">
        {isLoading && <p className="text-muted">Lade Matches…</p>}
        {error && <p className="text-status-double">Konnte Matches nicht laden</p>}

        {matches?.map((m, i) => (
          <FriendCard key={m.userId} slug={slug} match={m} top={i === 0 && m.score > 0} />
        ))}

        {matches?.length === 0 && !isLoading && (
          <div className="bg-surface border border-border rounded-[18px] p-6 text-center mt-4">
            <p className="font-sans font-extrabold text-text">Du bist allein im Album</p>
            <p className="text-sm text-muted mt-1.5">
              Lad jemanden ein — Invite-Code aus dem Profil.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function FriendCard({ slug, match, top }: { slug: string; match: MatchEntry; top: boolean }) {
  const palette = teamThemeOf(match.favoriteTeam);
  const muted = match.score === 0;

  if (top) {
    // Best-Partner-Card: prominent mit Akzent-Hintergrund + CTA
    return (
      <Link
        to={`/albums/${slug}/friends/${match.userId}`}
        className="block rounded-[18px] p-4 active:opacity-90 transition-opacity"
        style={{
          background: `linear-gradient(180deg, ${palette.primary}26, ${palette.primary}0a)`,
          border: `1px solid ${palette.primary}73`,
        }}
      >
        <div className="flex items-center gap-3.5">
          <span
            className="w-12 h-12 rounded-[15px] flex items-center justify-center font-sans font-extrabold text-[17px] text-white flex-none"
            style={{ background: palette.primary }}
          >
            {initialsOf(match.displayName)}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-sans text-[17px] font-extrabold text-text truncate">
                {match.displayName}
              </span>
              <span
                className="font-mono text-[8px] tracking-[.08em] text-white rounded-[5px] px-1.5 py-0.5"
                style={{ background: palette.primary }}
              >
                TOP
              </span>
            </div>
            <p className="font-mono text-[11px] text-muted mt-1">
              {match.iGive} gibst · {match.iGet} kriegst
            </p>
          </div>
          <span
            className="flex flex-col items-center justify-center w-[50px] h-[50px] rounded-[15px] flex-none"
            style={{ background: palette.primary }}
          >
            <span className="font-mono text-[21px] font-bold text-white leading-none">
              {match.score}
            </span>
            <span className="font-mono text-[7px] tracking-[.08em] text-white/85">
              MATCH
            </span>
          </span>
        </div>
        <div
          className="mt-3.5 py-3 rounded-[12px] bg-white text-center font-sans font-bold text-[13px]"
          style={{ color: palette.primary }}
        >
          Tausch ansehen →
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={muted ? '#' : `/albums/${slug}/friends/${match.userId}`}
      className={`flex items-center gap-3.5 p-3.5 bg-surface border border-border rounded-[16px] active:opacity-90 ${
        muted ? 'opacity-70 pointer-events-none' : ''
      }`}
    >
      <Avatar name={match.displayName} size={44} team={Boolean(match.favoriteTeam) && !muted} />
      <div className="flex-1 min-w-0">
        <p className="font-sans text-[15px] font-extrabold text-text truncate">
          {match.displayName}
        </p>
        <p className="font-mono text-[11px] text-muted mt-0.5">
          {muted ? 'noch kein Match' : `${match.iGive} gibst · ${match.iGet} kriegst`}
        </p>
      </div>
      <span
        className="flex items-center justify-center w-11 h-11 rounded-[13px] font-mono text-[17px] font-bold flex-none"
        style={{
          background: muted ? 'var(--color-surface-2)' : `${palette.primary}29`,
          color: muted ? 'var(--color-muted)' : palette.primary,
          border: muted ? '1px solid var(--color-border)' : `1px solid ${palette.primary}66`,
        }}
      >
        {match.score}
      </span>
    </Link>
  );
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';
}
